import { parsePesos, MoneyInputError } from "../money";
import { entryUnit, parseStockEntry, StockEntryError, type StockIngredient } from "./stock";

/**
 * "What came in" on a purchase of ingredients (T015, FR-129).
 *
 * One entry, not two: the money is recorded as spending and the quantities
 * raise stock, linked by the purchase's own id. Whoever unpacks a delivery
 * writes down what arrived once.
 *
 * The price per unit is optional here exactly as it is in inventory's own
 * Delivery form — a delivery whose invoice has not come is still a delivery.
 */

export interface StockLineDraft {
  key: string;
  ingredientId: string;
  quantity: string;
  price: string;
}

export interface ReceivedLine {
  ingredientId: string;
  quantity: number;
  unitCost: number | null;
}

let nextKey = 0;
export function emptyLine(): StockLineDraft {
  nextKey += 1;
  return { key: `line-${nextKey}`, ingredientId: "", quantity: "", price: "" };
}

/**
 * Drafts to lines, or an error naming the ingredient that is wrong. A row
 * nobody touched is not an error — it is the blank row at the bottom of the
 * list, and is simply dropped.
 */
export function parseStockLines(drafts: StockLineDraft[], stock: StockIngredient[]): ReceivedLine[] {
  const lines: ReceivedLine[] = [];

  for (const draft of drafts) {
    const untouched = draft.ingredientId === "" && draft.quantity.trim() === "" && draft.price.trim() === "";
    if (untouched) continue;

    const ingredient = stock.find((candidate) => candidate.id === draft.ingredientId);
    if (!ingredient) throw new Error("Choose what was received on every stock line, or clear the line.");

    let quantity: number;
    try {
      quantity = parseStockEntry(draft.quantity, ingredient.unit);
    } catch (caught) {
      throw new Error(
        caught instanceof StockEntryError ? `${ingredient.name}: ${caught.message}` : `Check ${ingredient.name}.`,
      );
    }
    if (quantity === 0) throw new Error(`Enter how much ${ingredient.name} came in.`);

    let unitCost: number | null = null;
    if (draft.price.trim() !== "") {
      try {
        unitCost = parsePesos(draft.price);
      } catch (caught) {
        throw new Error(
          caught instanceof MoneyInputError
            ? `${ingredient.name}: ${caught.message}`
            : `Check the price for ${ingredient.name}.`,
        );
      }
    }

    if (lines.some((line) => line.ingredientId === ingredient.id)) {
      throw new Error(`${ingredient.name} is on two lines — put it on one.`);
    }

    lines.push({ ingredientId: ingredient.id, quantity, unitCost });
  }

  return lines;
}

export interface StockLinesProps {
  stock: StockIngredient[];
  lines: StockLineDraft[];
  onChange(lines: StockLineDraft[]): void;
}

export function StockLines({ stock, lines, onChange }: StockLinesProps) {
  const update = (key: string, patch: Partial<StockLineDraft>) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  const remove = (key: string) => {
    const kept = lines.filter((line) => line.key !== key);
    onChange(kept.length === 0 ? [emptyLine()] : kept);
  };

  return (
    <fieldset className="books__lines">
      <legend className="books__subhead">What came in — optional</legend>
      <p className="books__hint">
        Add the ingredients delivered and stock rises with this purchase. One entry, not two.
      </p>

      {lines.map((line) => {
        const ingredient = stock.find((candidate) => candidate.id === line.ingredientId);
        return (
          <div className="books__line" key={line.key}>
            <label className="books__field">
              <span>Ingredient</span>
              <select
                value={line.ingredientId}
                onChange={(event) => update(line.key, { ingredientId: event.target.value })}
              >
                <option value="">Choose…</option>
                {stock.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="books__field">
              <span>{ingredient ? `Received (${entryUnit(ingredient.unit)})` : "Received"}</span>
              <input
                value={line.quantity}
                onChange={(event) => update(line.key, { quantity: event.target.value })}
                inputMode="decimal"
                disabled={!ingredient}
              />
            </label>

            <label className="books__field">
              <span>Price per {ingredient ? entryUnit(ingredient.unit) : "unit"} — optional</span>
              <input
                value={line.price}
                onChange={(event) => update(line.key, { price: event.target.value })}
                inputMode="decimal"
                disabled={!ingredient}
              />
            </label>

            <button
              type="button"
              className="books__action"
              onClick={() => remove(line.key)}
              aria-label="Remove this line"
            >
              ×
            </button>
          </div>
        );
      })}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={() => onChange([...lines, emptyLine()])}>
          + Another ingredient
        </button>
      </div>
    </fieldset>
  );
}
